<template>
  <v-container>
    <v-row
      class="mb-2"
      align="center"
    >
      <v-col>
        <h2>ຄົ້ນຫາດ້ວຍ Payment Reference (LDB)</h2>
        <div class="grey--text text--darken-1 text-body-2">
          ຖາມ LDB ວ່າເລກອ້າງອີງການໂອນຂອງທະນາຄານ ກົງກັບ QR (partnerOrderID) ໃດ
        </div>
      </v-col>
    </v-row>

    <v-card
      class="mb-4"
      elevation="2"
    >
      <v-card-text>
        <v-form @submit.prevent="search">
          <v-row align="center">
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model.trim="paymentReference"
                label="Payment Reference"
                placeholder="ເຊັ່ນ BONEBCTQ29L81L7V"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-combobox
                v-model="sendingBank"
                :items="banks"
                label="ທະນາຄານຜູ້ໂອນ (sendingBank)"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-btn
                color="primary"
                type="submit"
                block
                :loading="loading"
                :disabled="!canSearch"
              >
                <v-icon left>
                  mdi-magnify
                </v-icon>
                ຄົ້ນຫາ
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMsg"
      type="error"
      outlined
      dense
    >
      {{ errorMsg }}
    </v-alert>

    <v-row v-if="transaction">
      <v-col
        cols="12"
        md="6"
      >
        <v-card elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            ຂໍ້ມູນຈາກ LDB
            <v-chip
              small
              label
              dark
              class="ml-2"
              :color="statusInfo.color"
            >
              {{ transaction.processingStatus || '-' }} · {{ statusInfo.text }}
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-simple-table dense>
            <tbody>
              <tr
                v-for="row in transactionRows"
                :key="row.label"
              >
                <td class="grey--text text--darken-1">
                  {{ row.label }}
                </td>
                <td class="font-weight-medium">
                  {{ row.value }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="6"
      >
        <v-card elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            QR ໃນລະບົບເຮົາ (qr_generator)
          </v-card-title>
          <v-divider />
          <v-card-text v-if="!localQr">
            <v-alert
              type="warning"
              outlined
              dense
              class="mb-0"
            >
              ບໍ່ພົບ bill_number {{ transaction.partnerOrderID }} ໃນ qr_generator
            </v-alert>
          </v-card-text>
          <template v-else>
            <v-simple-table dense>
              <tbody>
                <tr
                  v-for="row in localQrRows"
                  :key="row.label"
                >
                  <td class="grey--text text--darken-1">
                    {{ row.label }}
                  </td>
                  <td class="font-weight-medium">
                    {{ row.value }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-alert
              v-if="amountMismatch"
              type="error"
              dense
              text
              class="ma-3"
            >
              ຈຳນວນເງິນບໍ່ກົງກັນ: LDB {{ fmtNumber(transaction.amount) }} / ລະບົບ {{ fmtNumber(localQr.amount) }}
            </v-alert>
            <v-card-actions>
              <v-btn
                text
                color="primary"
                :to="{ path: '/eod/qr-search', query: { bill_number: localQr.bill_number } }"
              >
                <v-icon left>
                  mdi-open-in-new
                </v-icon>
                ເປີດໃນໜ້າຄົ້ນຫາ QR
              </v-btn>
            </v-card-actions>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'LdbPaymentReferenceSearch',
  data() {
    return {
      paymentReference: '',
      sendingBank: 'BCEL',
      banks: ['BCEL', 'JDB', 'LDB', 'APB', 'LVB', 'STB', 'BIC', 'MJB'],
      loading: false,
      errorMsg: '',
      transaction: null,
      localQr: null,
    };
  },
  computed: {
    canSearch() {
      return /^[A-Za-z0-9]{6,64}$/.test(this.paymentReference || '')
        && /^[A-Za-z]{1,10}$/.test(this.sendingBank || '');
    },
    statusInfo() {
      // processingStatus values from LDB OpenAPI QR-Payment v1.0.06.
      const map = {
        RCVD: { text: 'LDB ໄດ້ຮັບລາຍການ', color: 'blue' },
        ACPT: { text: 'LDB ຮັບເອົາລາຍການ', color: 'blue' },
        FNLD: { text: 'ຊຳລະສຳເລັດ', color: 'green' },
        CNCL: { text: 'ຖືກຍົກເລີກ', color: 'grey' },
        RJCT: { text: 'ຖືກປະຕິເສດ', color: 'red' },
        EXPD: { text: 'ໝົດອາຍຸ', color: 'orange' },
      };
      const status = this.transaction && this.transaction.processingStatus;
      return map[status] || { text: 'ບໍ່ຮູ້ຈັກ', color: 'blue-grey' };
    },
    transactionRows() {
      const t = this.transaction || {};
      return [
        { label: 'partnerOrderID', value: t.partnerOrderID || '-' },
        { label: 'partnerPaymentID', value: t.partnerPaymentID || '-' },
        { label: 'paymentReference', value: t.paymentReference || '-' },
        { label: 'paymentBank', value: t.paymentBank || '-' },
        { label: 'paymentAt', value: t.paymentAt || '-' },
        { label: 'amount', value: `${this.fmtNumber(t.amount)} ${t.currency || ''}` },
      ];
    },
    localQrRows() {
      const q = this.localQr || {};
      const owner = q.owner || {};
      const customer = owner.customer || {};
      return [
        { label: 'ID', value: q.id },
        { label: 'Bill Number', value: q.bill_number + (q.deleted_at ? ' (ຖືກລຶບ)' : '') },
        { label: 'ສະຖານະ', value: q.status },
        { label: 'ລູກຄ້າ', value: [owner.name, owner.phone, customer.display_id].filter(Boolean).join(' · ') || '-' },
        { label: 'Billing IDs', value: (q.billing_ids || []).join(', ') || '-' },
        { label: 'ຈຳນວນເງິນ', value: `${this.fmtNumber(q.amount)} ${q.currency || ''}` },
        { label: 'ຊຳລະເມື່ອ', value: this.fmtDate(q.paid_at) },
        { label: 'ສ້າງເມື່ອ', value: this.fmtDate(q.created_at) },
      ];
    },
    amountMismatch() {
      return this.transaction && this.localQr
        && Number(this.transaction.amount) !== Number(this.localQr.amount);
    },
  },
  created() {
    const { payment_reference: ref, sending_bank: bank } = this.$route.query;
    if (bank) this.sendingBank = String(bank);
    if (ref) {
      this.paymentReference = String(ref);
      this.search();
    }
  },
  methods: {
    search() {
      if (!this.canSearch) return;

      const query = {
        payment_reference: this.paymentReference.toUpperCase(),
        sending_bank: String(this.sendingBank).toUpperCase(),
      };
      if (this.$route.query.payment_reference !== query.payment_reference
        || this.$route.query.sending_bank !== query.sending_bank) {
        this.$router.replace({ query }).catch(() => {});
      }

      this.loading = true;
      this.errorMsg = '';
      this.transaction = null;
      this.localQr = null;
      this.$axios
        .get('qr-generator/by-payment-reference', { params: query })
        .then((res) => {
          const data = res.data.data || {};
          this.transaction = data.transaction || null;
          this.localQr = data.local_qr || null;
        })
        .catch((err) => {
          const res = err.response;
          this.errorMsg = (res && res.data && res.data.message) || 'ຄົ້ນຫາບໍ່ສຳເລັດ';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fmtNumber(val) {
      if (val === null || val === undefined || val === '') return '-';
      return Intl.NumberFormat().format(Number(val));
    },
    fmtDate(val) {
      return val ? moment(val).format('DD-MM-YYYY HH:mm') : '-';
    },
  },
};
</script>
